/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import type * as vscode from 'vscode';
import { coalesce } from '../../../base/common/arrays.js';
import { DeferredPromise, raceCancellation, timeout } from '../../../base/common/async.js';
import { CancellationToken, CancellationTokenSource } from '../../../base/common/cancellation.js';
import { toErrorMessage } from '../../../base/common/errorMessage.js';
import { Emitter } from '../../../base/common/event.js';
import { Iterable } from '../../../base/common/iterator.js';
import { Disposable, DisposableMap, DisposableResourceMap, DisposableStore, toDisposable } from '../../../base/common/lifecycle.js';
import { revive } from '../../../base/common/marshalling.js';
import { StopWatch } from '../../../base/common/stopwatch.js';
import { assertType } from '../../../base/common/types.js';
import { URI, UriComponents } from '../../../base/common/uri.js';
import { generateUuid } from '../../../base/common/uuid.js';
import { Location } from '../../../editor/common/languages.js';
import { ExtensionIdentifier, IExtensionDescription, IRelaxedExtensionDescription } from '../../../platform/extensions/common/extensions.js';
import { ILogService } from '../../../platform/log/common/log.js';
import { packErrorForTelemetry } from '../../../platform/telemetry/common/errorTelemetry.js';
import { isChatViewTitleActionContext } from '../../contrib/chat/common/actions/chatActions.js';
import { IChatAgentRequest, IChatAgentResult, IChatAgentResultTimings, UserSelectedTools } from '../../contrib/chat/common/participants/chatAgents.js';
import { ChatAgentVoteDirection, IChatContentReference, IChatFollowup, IChatResponseErrorDetails, IChatUserActionEvent, IChatVoteAction } from '../../contrib/chat/common/chatService/chatService.js';
import { ChatRequestHooks } from '../../contrib/chat/common/promptSyntax/hookSchema.js';
import { LocalChatSessionUri } from '../../contrib/chat/common/model/chatUri.js';
import { ChatAgentLocation } from '../../contrib/chat/common/constants.js';
import { checkProposedApiEnabled, isProposedApiEnabled } from '../../services/extensions/common/extensions.js';
import { Dto } from '../../services/extensions/common/proxyIdentifier.js';
import { ExtHostChatAgentsShape2, IChatAgentCompletionItem, IChatAgentHistoryEntryDto, IChatAgentInvokeResult, IChatAgentProgressShape, IChatSessionCustomizationItemDto, IChatSessionCustomizationProviderMetadataDto, IChatProgressDto, IChatSessionContextDto, ICustomAgentDto, IExtensionChatAgentMetadata, IHookDto, IInstructionDto, IMainContext, IPluginDto, ISkillDto, ISlashCommandDto, MainContext, MainThreadChatAgentsShape2 } from './extHost.protocol.js';
import { CommandsConverter, ExtHostCommands } from './extHostCommands.js';
import { ExtHostDiagnostics } from './extHostDiagnostics.js';
import { ExtHostDocuments } from './extHostDocuments.js';
import { ExtHostLanguageModels } from './extHostLanguageModels.js';
import { ExtHostLanguageModelTools } from './extHostLanguageModelTools.js';
import * as typeConvert from './extHostTypeConverters.js';
import * as extHostTypes from './extHostTypes.js';
import { IPromptFileContext, IPromptFileResource } from '../../contrib/chat/common/promptSyntax/service/promptsService.js';
import { PromptsType } from '../../contrib/chat/common/promptSyntax/promptTypes.js';
import { ExtHostChatSessions } from './extHostChatSessions.js';
import { ExtHostDocumentsAndEditors } from './extHostDocumentsAndEditors.js';

export class ChatAgentResponseStream {

	private _stopWatch = StopWatch.create(false);
	private _isClosed: boolean = false;
	private _firstProgress: number | undefined;
	private _apiObject: vscode.ChatResponseStream | undefined;

	constructor(
		private readonly _extension: IExtensionDescription,
		private readonly _request: IChatAgentRequest,
		private readonly _proxy: IChatAgentProgressShape,
		private readonly _commandsConverter: CommandsConverter,
		private readonly _sessionDisposables: DisposableStore,
		private readonly _pendingCarouselResolvers: Map</* requestId */string, Map</* resolveId */ string, DeferredPromise<Record<string, unknown> | undefined>>>,
		private readonly _token: CancellationToken
	) { }

	close() {
		this._isClosed = true;
	}

	get timings(): IChatAgentResultTimings {
		return {
			firstProgress: this._firstProgress,
			totalElapsed: this._stopWatch.elapsed()
		};
	}

	get apiObject() {

		if (!this._apiObject) {

			const that = this;
			this._stopWatch.reset();


			let taskHandlePool = 0;


			function throwIfDone(source: Function | undefined) {
				if (that._isClosed) {
					const err = new Error('Response stream has been closed');
					Error.captureStackTrace(err, source);
					throw err;
				}
			}


			const sendQueue: (IChatProgressDto | [IChatProgressDto, number])[] = [];
			let notify: Function[] = [];

			function send(chunk: IChatProgressDto): void;
			function send(chunk: IChatProgressDto, handle: number): Promise<void>;
			function send(chunk: IChatProgressDto, handle?: number) {
				// push data into send queue. the first entry schedules the micro task which
				// does the actual send to the main thread
				const newLen = sendQueue.push(handle !== undefined ? [chunk, handle] : chunk);
				if (newLen === 1) {
					queueMicrotask(() => {
						const toNotify = notify;
						notify = [];
						that._proxy.$handleProgressChunk(that._request.requestId, sendQueue).finally(() => {
							toNotify.forEach(f => f());
						});
						sendQueue.length = 0;
					});
				}
				if (handle !== undefined) {
					return new Promise<void>(resolve => { notify.push(resolve); });
				}
				return;
			}

			const _report = (progress: IChatProgressDto, task?: (progress: vscode.Progress<vscode.ChatResponseWarningPart | vscode.ChatResponseReferencePart>) => Thenable<string | void>) => {
				// Measure the time to the first progress update with real markdown content
				if (typeof this._firstProgress === 'undefined' && (progress.kind === 'markdownContent' || progress.kind === 'markdownVuln' || progress.kind === 'beginToolInvocation')) {
					this._firstProgress = this._stopWatch.elapsed();
				}

				if (task) {
					const myHandle = taskHandlePool++;
					const progressReporterPromise = send(progress, myHandle);
					const progressReporter = {
						report: (p: vscode.ChatResponseWarningPart | vscode.ChatResponseReferencePart) => {
							progressReporterPromise.then(() => {
								if (extHostTypes.MarkdownString.isMarkdownString(p.value)) {
									send(typeConvert.ChatResponseWarningPart.from(<vscode.ChatResponseWarningPart>p), myHandle);
								} else {
									send(typeConvert.ChatResponseReferencePart.from(<vscode.ChatResponseReferencePart>p), myHandle);
								}
							});
						}
					};

					Promise.all([progressReporterPromise, task(progressReporter)]).then(([_void, res]) => {
						send(typeConvert.ChatTaskResult.from(res), myHandle);
					});
				} else {
					send(progress);
				}
			};

			this._apiObject = Object.freeze<vscode.ChatResponseStream>({
				clearToPreviousToolInvocation(reason) {
					throwIfDone(this.markdown);
					send({ kind: 'clearToPreviousToolInvocation', reason: reason });
					return this;
				},
				markdown(value) {
					throwIfDone(this.markdown);
					const part = new extHostTypes.ChatResponseMarkdownPart(value);
					const dto = typeConvert.ChatResponseMarkdownPart.from(part);
					_report(dto);
					return this;
				},
				markdownWithVulnerabilities(value, vulnerabilities) {
					throwIfDone(this.markdown);
					if (vulnerabilities) {
						checkProposedApiEnabled(that._extension, 'chatParticipantAdditions');
					}

					const part = new extHostTypes.ChatResponseMarkdownWithVulnerabilitiesPart(value, vulnerabilities);
					const dto = typeConvert.ChatResponseMarkdownWithVulnerabilitiesPart.from(part);
					_report(dto);
					return this;
				},
				codeblockUri(value, isEdit) {
					throwIfDone(this.codeblockUri);
					checkProposedApiEnabled(that._extension, 'chatParticipantAdditions');
					const part = new extHostTypes.ChatResponseCodeblockUriPart(value, isEdit);
					const dto = typeConvert.ChatResponseCodeblockUriPart.from(part);
					_report(dto);
					return this;
				},
				filetree(value, baseUri) {
					throwIfDone(this.filetree);
					const part = new extHostTypes.ChatResponseFileTreePart(value, baseUri);
					const dto = typeConvert.ChatResponseFilesPart.from(part);
					_report(dto);
					return this;
				},
				anchor(value, title?: string) {
					const part = new extHostTypes.ChatResponseAnchorPart(value, title);
					return this.push(part);
				},
				button(value) {
					throwIfDone(this.anchor);
					const part = new extHostTypes.ChatResponseCommandButtonPart(value);
					const dto = typeConvert.ChatResponseCommandButtonPart.from(part, that._commandsConverter, that._sessionDisposables);
					_report(dto);
					return this;
				},
				progress(value, task?: ((progress: vscode.Progress<vscode.ChatResponseWarningPart>) => Thenable<string | void>)) {
					throwIfDone(this.progress);
					const part = new extHostTypes.ChatResponseProgressPart2(value, task);
					const dto = task ? typeConvert.ChatTask.from(part) : typeConvert.ChatResponseProgressPart.from(part);
					_report(dto, task);
					return this;
				},
				thinkingProgress(thinkingDelta: vscode.ThinkingDelta) {
					throwIfDone(this.thinkingProgress);
					checkProposedApiEnabled(that._extension, 'chatParticipantAdditions');
					const part = new extHostTypes.ChatResponseThinkingProgressPart(thinkingDelta.text ?? '', thinkingDelta.id, thinkingDelta.metadata);
					const dto = typeConvert.ChatResponseThinkingProgressPart.from(part);
					_report(dto);
					return this;
				},
				hookProgress(hookType: vscode.ChatHookType, stopReason?: string, systemMessage?: string) {
					throwIfDone(this.hookProgress);
					checkProposedApiEnabled(that._extension, 'chatParticipantAdditions');
					const part = new extHostTypes.ChatResponseHookPart(hookType, stopReason, systemMessage);
					const dto = typeConvert.ChatResponseHookPart.from(part);
					_report(dto);
					return this;
				},
				warning(value) {
					throwIfDone(this.progress);
					checkProposedApiEnabled(that._extension, 'chatParticipantAdditions');
					const part = new extHostTypes.ChatResponseWarningPart(value);
					const dto = typeConvert.ChatResponseWarningPart.from(part);
					_report(dto);
					return this;
				},
				reference(value, iconPath) {
					return this.reference2(value, iconPath);
				},
				reference2(value, iconPath, options) {
					throwIfDone(this.reference);

					if (typeof value === 'object' && 'variableName' in value) {
						checkProposedApiEnabled(that._extension, 'chatParticipantAdditions');
					}

					if (typeof value === 'object' && 'variableName' in value && !value.value) {
						// The participant used this variable. Does that variable have any references to pull in?
						const matchingVarData = that._request.variables.variables.find(v => v.name === value.variableName);
						if (matchingVarData) {
							let references: Dto<IChatContentReference>[] | undefined;
							if (matchingVarData.references?.length) {
								references = matchingVarData.references.map(r => ({
									kind: 'reference',
									reference: { variableName: value.variableName, value: r.reference as URI | Location }
								} satisfies IChatContentReference));
							} else {
								// Participant sent a variableName reference but the variable produced no references. Show variable reference with no value
								const part = new extHostTypes.ChatResponseReferencePart(value, iconPath, options);
								const dto = typeConvert.ChatResponseReferencePart.from(part);
								references = [dto];
							}

							references.forEach(r => _report(r));
							return this;
						} else {
							// Something went wrong- that variable doesn't actually exist
						}
					} else {
						const part = new extHostTypes.ChatResponseReferencePart(value, iconPath, options);
						const dto = typeConvert.ChatResponseReferencePart.from(part);
						_report(dto);
					}

					return this;
				},
				codeCitation(value: vscode.Uri, license: string, snippet: string): void {
					throwIfDone(this.codeCitation);
					checkProposedApiEnabled(that._extension, 'chatParticipantAdditions');

					const part = new extHostTypes.ChatResponseCodeCitationPart(value, license, snippet);
					const dto = typeConvert.ChatResponseCodeCitationPart.from(part);
					_report(dto);
				},
				textEdit(target, edits) {
					throwIfDone(this.textEdit);
					checkProposedApiEnabled(that._extension, 'chatParticipantAdditions');

					const part = new extHostTypes.ChatResponseTextEditPart(target, edits);
					part.isDone = edits === true ? true : undefined;
					const dto = typeConvert.ChatResponseTextEditPart.from(part);
					_report(dto);
					return this;
				},
				notebookEdit(target, edits) {
					throwIfDone(this.notebookEdit);
					checkProposedApiEnabled(that._extension, 'chatParticipantAdditions');

					const part = new extHostTypes.ChatResponseNotebookEditPart(target, edits);
					const dto = typeConvert.ChatResponseNotebookEditPart.from(part);
					_report(dto);
					return this;
				},
				workspaceEdit(edits) {
					throwIfDone(this.workspaceEdit);
					checkProposedApiEnabled(that._extension, 'chatParticipantAdditions');

					const part = new extHostTypes.ChatResponseWorkspaceEditPart(edits);
					const dto = typeConvert.ChatResponseWorkspaceEditPart.from(part);
					_report(dto);
					return this;
				},
				async externalEdit(target, callback) {
					throwIfDone(this.externalEdit);
					const resources = Array.isArray(target) ? target : [target];
					const operationId = taskHandlePool++;
					const undoStopId = generateUuid();
					await send({ kind: 'externalEdits', start: true, resources, undoStopId }, operationId);
					try {
						await callback();
						return undoStopId;
					} finally {
						await send({ kind: 'externalEdits', start: false, resources, undoStopId }, operationId);
					}
				},
				confirmation(title, message, data, buttons) {
					throwIfDone(this.confirmation);
					checkProposedApiEnabled(that._extension, 'chatParticipantAdditions');

					const part = new extHostTypes.ChatResponseConfirmationPart(title, message, data, buttons);
					const dto = typeConvert.ChatResponseConfirmationPart.from(part);
					_report(dto);
					return this;
				},
				async questionCarousel(questions: vscode.ChatQuestion[], allowSkip = true): Promise<Record<string, unknown> | undefined> {
					throwIfDone(this.questionCarousel);
					checkProposedApiEnabled(that._extension, 'chatParticipantAdditions');

					const resolveId = generateUuid();
					const part = new extHostTypes.ChatResponseQuestionCarouselPart(questions, allowSkip);
					const dto = typeConvert.ChatResponseQuestionCarouselPart.from(part);
					dto.resolveId = resolveId;

					// Create a deferred promise to wait for the answer
					const deferred = new DeferredPromise<Record<string, unknown> | undefined>();

					// Store the deferred promise for later resolution
					if (!that._pendingCarouselResolvers.has(that._request.requestId)) {
						that._pendingCarouselResolvers.set(that._request.requestId, new Map());
					}
					that._pendingCarouselResolvers.get(that._request.requestId)!.set(resolveId, deferred);

					_report(dto);

					// Wait for the user to submit answers, but respect cancellation
					return raceCancellation(deferred.p, that._token);
				},
				beginToolInvocation(toolCallId, toolName, streamData) {
					throwIfDone(this.beginToolInvocation);
					checkProposedApiEnabled(that._extension, 'chatParticipantAdditions');

					const dto: IChatProgressDto = {
						kind: 'beginToolInvocation',
						toolCallId,
						toolName,
						streamData: streamData ? {
							partialInput: streamData.partialInput
						} : undefined,
						subagentInvocationId: streamData?.subagentInvocationId,
					};
					_report(dto);
					return this;
				},
				updateToolInvocation(toolCallId, streamData) {
					throwIfDone(this.updateToolInvocation);
					checkProposedApiEnabled(that._extension, 'chatParticipantAdditions');

					const dto: IChatProgressDto = {
						kind: 'updateToolInvocation',
						toolCallId,
						streamData: {
							partialInput: streamData.partialInput
						}
					};
					_report(dto);
					return this;
				},
				push(part) {
					throwIfDone(this.push);

					if (
						part instanceof extHostTypes.ChatResponseTextEditPart ||
						part instanceof extHostTypes.ChatResponseNotebookEditPart ||
						part instanceof extHostTypes.ChatResponseMarkdownWithVulnerabilitiesPart ||
						part instanceof extHostTypes.ChatResponseWarningPart ||
						part instanceof extHostTypes.ChatResponseConfirmationPart ||
						part instanceof extHostTypes.ChatResponseQuestionCarouselPart ||
						part instanceof extHostTypes.ChatResponseCodeCitationPart ||
						part instanceof extHostTypes.ChatResponseMovePart ||
						part instanceof extHostTypes.ChatResponseExtensionsPart ||
						part instanceof extHostTypes.ChatResponseExternalEditPart ||
						part instanceof extHostTypes.ChatResponseThinkingProgressPart ||
						part instanceof extHostTypes.ChatResponsePullRequestPart ||
						part instanceof extHostTypes.ChatResponseProgressPart2
					) {
						checkProposedApiEnabled(that._extension, 'chatParticipantAdditions');
					}

					if (part instanceof extHostTypes.ChatResponseReferencePart) {
						// Ensure variable reference values get fixed up
						this.reference2(part.value, part.iconPath, part.options);
					} else if (part instanceof extHostTypes.ChatResponseProgressPart2) {
						const dto = part.task ? typeConvert.ChatTask.from(part) : typeConvert.ChatResponseProgressPart.from(part);
						_report(dto, part.task);
					} else if (part instanceof extHostTypes.ChatResponseThinkingProgressPart) {
						const dto = typeConvert.ChatResponseThinkingProgressPart.from(part);
						_report(dto);
					} else if (part instanceof extHostTypes.ChatResponseAnchorPart) {
						const dto = typeConvert.ChatResponseAnchorPart.from(part);

						if (part.resolve) {
							checkProposedApiEnabled(that._extension, 'chatParticipantAdditions');

							dto.resolveId = generateUuid();

							const cts = new CancellationTokenSource();
							part.resolve(cts.token)
								.then(() => {
									const resolvedDto = typeConvert.ChatResponseAnchorPart.from(part);
									that._proxy.$handleAnchorResolve(that._request.requestId, dto.resolveId!, resolvedDto);
								})
								.then(() => cts.dispose(), () => cts.dispose());
							that._sessionDisposables.add(toDisposable(() => cts.dispose(true)));
						}
						_report(dto);
					} else if (part instanceof extHostTypes.ChatResponseExternalEditPart) {
						const p = this.externalEdit(part.uris, part.callback);
						p.then((value) => part.didGetApplied(value));
						return this;
					} else {
						const dto = typeConvert.ChatResponsePart.from(part, that._commandsConverter, that._sessionDisposables);
						_report(dto);
					}

					return this;
				},
				usage(usage) {
					throwIfDone(this.usage);
					checkProposedApiEnabled(that._extension, 'chatParticipantAdditions');

					const dto: IChatProgressDto = {
						kind: 'usage',
						promptTokens: usage.promptTokens,
						completionTokens: usage.completionTokens,
						outputBuffer: usage.outputBuffer,
						promptTokenDetails: usage.promptTokenDetails
					};
					_report(dto);
					return this;
				},
			});
		}

		return this._apiObject;
	}
}

interface InFlightChatRequest {
	requestId: string;
	extRequest: vscode.ChatRequest;
	extension: IRelaxedExtensionDescription;
	hooks?: ChatRequestHooks;
	yieldRequested: boolean;
}

export class ExtHostChatAgents2 extends Disposable implements ExtHostChatAgentsShape2 {

	private static _idPool = 0;

	private readonly _agents = new Map<number, ExtHostChatAgent>();
	private readonly _proxy: MainThreadChatAgentsShape2;

	private static _participantDetectionProviderIdPool = 0;
	private readonly _participantDetectionProviders = new Map<number, ExtHostParticipantDetector>();

	private static _contributionsProviderIdPool = 0;
	private readonly _promptFileProviders = new Map<number, { extension: IExtensionDescription; provider: vscode.ChatCustomAgentProvider | vscode.ChatInstructionsProvider | vscode.ChatPromptFileProvider | vscode.ChatSkillProvider | vscode.ChatHookProvider }>();

	private static _customizationProviderIdPool = 0;
	private readonly _customizationProviders = new Map<number, { extension: IExtensionDescription; provider: vscode.ChatSessionCustomizationProvider }>();

	private readonly _sessionDisposables: DisposableResourceMap<DisposableStore> = this._register(new DisposableResourceMap());
	private readonly _completionDisposables: DisposableMap<number, DisposableStore> = this._register(new DisposableMap());

	private readonly _inFlightRequests = new Set<InFlightChatRequest>();

	// Map of requestId -> resolveId -> deferred promise for question carousel answers
	private readonly _pendingCarouselResolvers = new Map<string, Map<string, DeferredPromise<Record<string, unknown> | undefined>>>();

	private readonly _onDidChangeChatRequestTools = this._register(new Emitter<vscode.ChatRequest>());
	readonly onDidChangeChatRequestTools = this._onDidChangeChatRequestTools.event;

	private readonly _onDidDisposeChatSession = this._register(new Emitter<string>());
	readonly onDidDisposeChatSession = this._onDidDisposeChatSession.event;

	private readonly _onDidChangeCustomAgents = this._register(new Emitter<void>());
	readonly onDidChangeCustomAgents = this._onDidChangeCustomAgents.event;
	private readonly _onDidChangeInstructions = this._register(new Emitter<void>());
	readonly onDidChangeInstructions = this._onDidChangeInstructions.event;
	private readonly _onDidChangeSkills = this._register(new Emitter<void>());
	readonly onDidChangeSkills = this._onDidChangeSkills.event;
	private readonly _onDidChangeSlashCommands = this._register(new Emitter<void>());
	readonly onDidChangeSlashCommands = this._onDidChangeSlashCommands.event;
	private readonly _onDidChangeHooks = this._register(new Emitter<void>());
	readonly onDidChangeHooks = this._onDidChangeHooks.event;
	private readonly _onDidChangePlugins = this._register(new Emitter<void>());
	readonly onDidChangePlugins = this._onDidChangePlugins.event;

	private _customAgents: vscode.ChatCustomAgent[] = [];
	private _instructions: vscode.ChatInstruction[] = [];
	private _skills: vscode.ChatSkill[] = [];
	private _slashCommands: vscode.ChatSlashCommand[] = [];
	private _hooks: vscode.ChatHook[] = [];
	private _plugins: vscode.ChatPlugin[] = [];

	private _activeChatPanelSessionResource: URI | undefined;

	private readonly _onDidChangeActiveChatPanelSessionResource = this._register(new Emitter<URI | undefined>());
	readonly onDidChangeActiveChatPanelSessionResource = this._onDidChangeActiveChatPanelSessionResource.event;

	get activeChatPanelSessionResource(): URI | undefined {
		return this._activeChatPanelSessionResource;
	}

	get customAgents(): readonly vscode.ChatCustomAgent[] {
		return this._customAgents;
	}

	get instructions(): readonly vscode.ChatInstruction[] {
		return this._instructions;
	}

	get skills(): readonly vscode.ChatSkill[] {
		return this._skills;
	}

	get slashCommands(): readonly vscode.ChatSlashCommand[] {
		return this._slashCommands;
	}

	private toCustomAgent(dto: ICustomAgentDto): vscode.ChatCustomAgent {
		return Object.freeze<vscode.ChatCustomAgent>({
			uri: URI.revive(dto.uri),
			name: dto.name,
			description: dto.description,
			source: dto.source,
			extensionId: dto.extensionId,
			pluginUri: dto.pluginUri ? URI.revive(dto.pluginUri) : undefined,
			argumentHint: dto.argumentHint,
			tools: dto.tools,
			model: dto.model,
			userInvocable: dto.userInvocable,
			disableModelInvocation: dto.disableModelInvocation,
		});
	}

	private toInstruction(dto: IInstructionDto): vscode.ChatInstruction {
		return Object.freeze<vscode.ChatInstruction>({
			uri: URI.revive(dto.uri),
			name: dto.name,
			description: dto.description,
			source: dto.source,
			extensionId: dto.extensionId,
			pluginUri: dto.pluginUri ? URI.revive(dto.pluginUri) : undefined,
			pattern: dto.pattern,
		});
	}

	private toSkill(dto: ISkillDto): vscode.ChatSkill {
		return Object.freeze<vscode.ChatSkill>({
			uri: URI.revive(dto.uri),
			name: dto.name,
			description: dto.description,
			source: dto.source,
			extensionId: dto.extensionId,
			pluginUri: dto.pluginUri ? URI.revive(dto.pluginUri) : undefined,
			userInvocable: dto.userInvocable,
		});
	}

	private toSlashCommand(dto: ISlashCommandDto): vscode.ChatSlashCommand {
		return Object.freeze<vscode.ChatSlashCommand>({
			uri: URI.revive(dto.uri),
			name: dto.name,
			description: dto.description,
			source: dto.source,
			extensionId: dto.extensionId,
			pluginUri: dto.pluginUri ? URI.revive(dto.pluginUri) : undefined,
			argumentHint: dto.argumentHint,
			userInvocable: dto.userInvocable,
		});
	}

	private toHook(dto: IHookDto): vscode.ChatHook {
		return Object.freeze({ uri: URI.revive(dto.uri) });
	}

	private toPlugin(dto: IPluginDto): vscode.ChatPlugin {
		return Object.freeze({ uri: URI.revive(dto.uri) });
	}

	get hooks(): readonly vscode.ChatHook[] {
		return this._hooks;
	}

	get plugins(): readonly vscode.ChatPlugin[] {
		return this._plugins;
	}

	provideCustomAgents(token: vscode.CancellationToken): Thenable<readonly vscode.ChatCustomAgent[]> {
		return this._proxy.$provideCustomAgents(token).then(agents => this._customAgents = agents.map(agent => this.toCustomAgent(agent)));
	}

	provideInstructions(token: vscode.CancellationToken): Thenable<readonly vscode.ChatInstruction[]> {
		return this._proxy.$provideInstructions(token).then(instructions => this._instructions = instructions.map(instruction => this.toInstruction(instruction)));
	}

	provideSkills(token: vscode.CancellationToken): Thenable<readonly vscode.ChatSkill[]> {
		return this._proxy.$provideSkills(token).then(skills => this._skills = skills.map(skill => this.toSkill(skill)));
	}

	provideSlashCommands(token: vscode.CancellationToken): Thenable<readonly vscode.ChatSlashCommand[]> {
		return this._proxy.$provideSlashCommands(token).then(slashCommands => this._slashCommands = slashCommands.map(slashCommand => this.toSlashCommand(slashCommand)));
	}

	provideHooks(token: vscode.CancellationToken): Thenable<readonly vscode.ChatHook[]> {
		return this._proxy.$provideHooks(token).then(hooks => this._hooks = hooks.map(hook => this.toHook(hook)));
	}

	providePlugins(token: vscode.CancellationToken): Thenable<readonly vscode.ChatPlugin[]> {
		return this._proxy.$providePlugins(token).then(plugins => this._plugins = plugins.map(plugin => this.toPlugin(plugin)));
	}

	$acceptCustomAgents(agents: ICustomAgentDto[]): void {
		this._customAgents = agents.map(agent => this.toCustomAgent(agent));
		this._onDidChangeCustomAgents.fire();
	}

	$acceptInstructions(instructions: IInstructionDto[]): void {
		this._instructions = instructions.map(instruction => this.toInstruction(instruction));
		this._onDidChangeInstructions.fire();
	}

	$acceptSkills(skills: ISkillDto[]): void {
		this._skills = skills.map(skill => this.toSkill(skill));
		this._onDidChangeSkills.fire();
	}

	$acceptSlashCommands(slashCommands: ISlashCommandDto[]): void {
		this._slashCommands = slashCommands.map(slashCommand => this.toSlashCommand(slashCommand));
		this._onDidChangeSlashCommands.fire();
	}

	$acceptHooks(hooks: IHookDto[]): void {
		this._hooks = hooks.map(hook => this.toHook(hook));
		this._onDidChangeHooks.fire();
	}

	$acceptPlugins(plugins: IPluginDto[]): void {
		this._plugins = plugins.map(plugin => this.toPlugin(plugin));
		this._onDidChangePlugins.fire();
	}

	constructor(
		mainContext: IMainContext,
		private readonly _logService: ILogService,
		private readonly _commands: ExtHostCommands,
		private readonly _documents: ExtHostDocuments,
		private readonly _editorsAndDocuments: ExtHostDocumentsAndEditors,
		private readonly _languageModels: ExtHostLanguageModels,
		private readonly _diagnostics: ExtHostDiagnostics,
		private readonly _tools: ExtHostLanguageModelTools,
		private readonly _chatSessions: ExtHostChatSessions,
	) {
		super();
		this._proxy = mainContext.getProxy(MainContext.MainThreadChatAgents2);

		_commands.registerArgumentProcessor({
			processArgument: (arg) => {
				// Don't send this argument to extension commands
				if (isChatViewTitleActionContext(arg)) {
					return null;
				}

				return arg;
			}
		});
	}

	async transferActiveChat(newWorkspace: vscode.Uri): Promise<void> {
		await this._proxy.$transferActiveChatSession(newWorkspace);
	}

	createChatAgent(extension: IExtensionDescription, id: string, handler: vscode.ChatExtendedRequestHandler): vscode.ChatParticipant {
		const handle = ExtHostChatAgents2._idPool++;
		const agent = new ExtHostChatAgent(extension, id, this._proxy, handle, handler);
		this._agents.set(handle, agent);

		this._proxy.$registerAgent(handle, extension.identifier, id, {}, undefined);
		return agent.apiAgent;
	}

	createDynamicChatAgent(extension: IExtensionDescription, id: string, dynamicProps: vscode.DynamicChatParticipantProps, handler: vscode.ChatExtendedRequestHandler): vscode.ChatParticipant {
		const handle = ExtHostChatAgents2._idPool++;
		const agent = new ExtHostChatAgent(extension, id, this._proxy, handle, handler);
		this._agents.set(handle, agent);

		this._proxy.$registerAgent(handle, extension.identifier, id, { isSticky: true } satisfies IExtensionChatAgentMetadata, dynamicProps);
		return agent.apiAgent;
	}

	registerChatParticipantDetectionProvider(extension: IExtensionDescription, provider: vscode.ChatParticipantDetectionProvider): vscode.Disposable {
		const handle = ExtHostChatAgents2._participantDetectionProviderIdPool++;
		this._participantDetectionProviders.set(handle, new ExtHostParticipantDetector(extension, provider));
		this._proxy.$registerChatParticipantDetectionProvider(handle);
		return toDisposable(() => {
			this._participantDetectionProviders.delete(handle);
			this._proxy.$unregisterChatParticipantDetectionProvider(handle);
		});
	}

	/**
	 * Internal method that handles all prompt file provider types.
	 * Routes custom agents, instructions, prompt files, and skills to the unified internal implementation.
	 */
	registerPromptFileProvider(extension: IExtensionDescription, type: PromptsType, provider: vscode.ChatCustomAgentProvider | vscode.ChatInstructionsProvider | vscode.ChatPromptFileProvider | vscode.ChatSkillProvider | vscode.ChatHookProvider): vscode.Disposable {
		const handle = ExtHostChatAgents2._contributionsProviderIdPool++;
		this._promptFileProviders.set(handle, { extension, provider });
		this._proxy.$registerPromptFileProvider(handle, type, extension.identifier);

		const disposables = new DisposableStore();

		// Listen to provider change events and notify main thread
		// Check for the appropriate event based on the provider type
		let changeEvent: vscode.Event<void> | undefined;
		switch (type) {
			case PromptsType.agent:
				changeEvent = (provider as vscode.ChatCustomAgentProvider).onDidChangeCustomAgents;
				break;
			case PromptsType.instructions:
				changeEvent = (provider as vscode.ChatInstructionsProvider).onDidChangeInstructions;
				break;
			case PromptsType.prompt:
				changeEvent = (provider as vscode.ChatPromptFileProvider).onDidChangePromptFiles;
				break;
			case PromptsType.skill:
				changeEvent = (provider as vscode.ChatSkillProvider).onDidChangeSkills;
				break;
			case PromptsType.hook:
				changeEvent = (provider as vscode.ChatHookProvider).onDidChangeHooks;
				break;
		}

		if (changeEvent) {
			disposables.add(changeEvent(() => {
				this._proxy.$onDidChangePromptFiles(handle);
			}));
		}

		disposables.add(toDisposable(() => {
			this._promptFileProviders.delete(handle);
			this._proxy.$unregisterPromptFileProvider(handle);
		}));

		return disposables;
	}

	async $providePromptFiles(handle: number, type: PromptsType, context: IPromptFileContext, token: CancellationToken): Promise<IPromptFileResource[] | undefined> {
		const providerData = this._promptFileProviders.get(handle);
		if (!providerData) {
			return undefined;
		}

		const provider = providerData.provider;
		let resources: vscode.ChatResource[] | undefined;
		switch (type) {
			case PromptsType.agent:
				resources = await (provider as vscode.ChatCustomAgentProvider).provideCustomAgents(context, token) ?? undefined;
				break;
			case PromptsType.instructions:
				resources = await (provider as vscode.ChatInstructionsProvider).provideInstructions(context, token) ?? undefined;
				break;
			case PromptsType.prompt:
				resources = await (provider as vscode.ChatPromptFileProvider).providePromptFiles(context, token) ?? undefined;
				break;
			case PromptsType.skill:
				resources = await (provider as vscode.ChatSkillProvider).provideSkills(context, token) ?? undefined;
				break;
			case PromptsType.hook:
				resources = await (provider as vscode.ChatHookProvider).provideHooks(context, token) ?? undefined;
				break;
		}

		return resources;
	}

	registerChatSessionCustomizationProvider(extension: IExtensionDescription, chatSessionType: string, metadata: vscode.ChatSessionCustomizationProviderMetadata, provider: vscode.ChatSessionCustomizationProvider): vscode.Disposable {
		const handle = ExtHostChatAgents2._customizationProviderIdPool++;
		this._customizationProviders.set(handle, { extension, provider });

		const metadataDto: IChatSessionCustomizationProviderMetadataDto = {
			label: metadata.label,
			iconId: metadata.iconId,
			supportedTypes: metadata.supportedTypes?.map(t => typeConvert.ChatSessionCustomizationType.from(t)),
		};

		this._proxy.$registerChatSessionCustomizationProvider(handle, chatSessionType, metadataDto, extension.identifier);

		const disposables = new DisposableStore();

		if (provider.onDidChange) {
			disposables.add(provider.onDidChange(() => {
				this._proxy.$onDidChangeCustomizations(handle);
			}));
		}

		disposables.add(toDisposable(() => {
			this._customizationProviders.delete(handle);
			this._proxy.$unregisterChatSessionCustomizationProvider(handle);
		}));

		return disposables;
	}

	async $provideChatSessionCustomizations(handle: number, token: CancellationToken): Promise<IChatSessionCustomizationItemDto[] | undefined> {
		const providerData = this._customizationProviders.get(handle);
		if (!providerData) {
			return undefined;
		}

		try {
			const items = await providerData.provider.provideChatSessionCustomizations(token);
			if (!items) {
				return undefined;
			}

			return items.map(item => ({
				uri: item.uri,
				type: typeConvert.ChatSessionCustomizationType.from(item.type),
				name: item.name,
				description: item.description,
				groupKey: item.groupKey,
				badge: item.badge,
				badgeTooltip: item.badgeTooltip,
			}));
		} catch (err) {
			return undefined;
		}
	}

	async $detectChatParticipant(handle: number, requestDto: Dto<IChatAgentRequest>, context: { history: IChatAgentHistoryEntryDto[] }, options: { location: ChatAgentLocation; participants?: vscode.ChatParticipantMetadata[] }, token: CancellationToken): Promise<vscode.ChatParticipantDetectionResult | null | undefined> {
		const detector = this._participantDetectionProviders.get(handle);
		if (!detector) {
			return undefined;
		}

		const { request, location, history } = await this._createRequest(requestDto, context, detector.extension);

		const model = await this.getModelForRequest(request, detector.extension);
		const tools = await this.getToolsForRequest(detector.extension, request.userSelectedTools, model.id, token);
		const extRequest = typeConvert.ChatAgentRequest.to(
			request,
			location,
			model,
			request.modelConfiguration,
			this.getDiagnosticsWhenEnabled(detector.extension),
			tools,
			detector.extension,
			this._logService);

		return detector.provider.provideParticipantDetection(
			extRequest,
			{ history, yieldRequested: false },
			{ participants: options.participants, location: typeConvert.ChatLocation.to(options.location) },
			token
		);
	}

	private async _createRequest(requestDto: Dto<IChatAgentRequest>, context: { history: IChatAgentHistoryEntryDto[] }, extension: IExtensionDescription) {
		const request = revive<IChatAgentRequest>(requestDto);
		const convertedHistory = await this.prepareHistoryTurns(extension, request.agentId, context);

		// in-place converting for location-data
		let location: vscode.ChatRequestEditorData | vscode.ChatRequestNotebookData | undefined;
		if (request.locationData?.type === ChatAgentLocation.EditorInline) {
			// editor data
			const document = this._documents.getDocument(request.locationData.document);
			const editor = this._editorsAndDocuments.getEditor(request.locationData.id)!;
			location = new extHostTypes.ChatRequestEditorData(editor.value, document, typeConvert.Selection.to(request.locationData.selection), typeConvert.Range.to(request.locationData.wholeRange));

		} else if (request.locationData?.type === ChatAgentLocation.Notebook) {
			// notebook data
			const cell = this._documents.getDocument(request.locationData.sessionInputUri);
			location = new extHostTypes.ChatRequestNotebookData(cell);

		} else if (request.locationData?.type === ChatAgentLocation.Terminal) {
			// TBD
		}

		return { request, location, history: convertedHistory };
	}

	private async getModelForRequest(request: IChatAgentRequest, extension: IExtensionDescription): Promise<vscode.LanguageModelChat> {
		let model: vscode.LanguageModelChat | undefined;
		if (request.userSelectedModelId) {
			model = await this._languageModels.getLanguageModelByIdentifier(extension, request.userSelectedModelId);
		}
		if (!model) {
			model = await this._languageModels.getDefaultLanguageModel(extension);
			if (!model) {
				throw new Error('Language model unavailable');
			}
		}

		return model;
	}


	async $setRequestTools(requestId: string, tools: UserSelectedTools) {
		const request = [...this._inFlightRequests].find(r => r.requestId === requestId);
		if (!request) {
			return;
		}

		request.extRequest.tools.clear();
		const toolsMap = await this.getToolsForRequest(request.extension, tools, request.extRequest.model.id, CancellationToken.None);
		for (const [k, v] of toolsMap) {
			request.extRequest.tools.set(k, v);
		}
		this._onDidChangeChatRequestTools.fire(request.extRequest);
	}

	$setYieldRequested(requestId: string, value: boolean): void {
		const request = [...this._inFlightRequests].find(r => r.requestId === requestId);
		if (request) {
			request.yieldRequested = value;
		}
	}

	async $invokeAgent(handle: number, requestDto: Dto<IChatAgentRequest>, context: { history: IChatAgentHistoryEntryDto[]; chatSessionContext?: IChatSessionContextDto }, token: CancellationToken): Promise<IChatAgentInvokeResult | undefined> {
		const agent = this._agents.get(handle);
		if (!agent) {
			throw new Error(`[CHAT](${handle}) CANNOT invoke agent because the agent is not registered`);
		}

		let stream: ChatAgentResponseStream | undefined;
		let inFlightRequest: InFlightChatRequest | undefined;

		try {
			const { request, location, history } = await this._createRequest(requestDto, context, agent.extension);

			// Init session disposables
			let sessionDisposables = this._sessionDisposables.get(request.sessionResource);
			if (!sessionDisposables) {
				sessionDisposables = new DisposableStore();
				this._sessionDisposables.set(request.sessionResource, sessionDisposables);
			}

			stream = new ChatAgentResponseStream(agent.extension, request, this._proxy, this._commands.converter, sessionDisposables, this._pendingCarouselResolvers, token);

			const model = await this.getModelForRequest(request, agent.extension);
			const tools = await this.getToolsForRequest(agent.extension, request.userSelectedTools, model.id, token);
			const extRequest = typeConvert.ChatAgentRequest.to(
				request,
				location,
				model,
				request.modelConfiguration,
				this.getDiagnosticsWhenEnabled(agent.extension),
				tools,
				agent.extension,
				this._logService
			);
			inFlightRequest = { requestId: requestDto.requestId, extRequest, extension: agent.extension, hooks: request.hooks, yieldRequested: false };
			this._inFlightRequests.add(inFlightRequest);


			// If this request originates from a contributed chat session editor, attempt to resolve the ChatSession API object
			let chatSessionContext: vscode.ChatSessionContext | undefined;
			if (context.chatSessionContext) {
				const sessionResource = URI.revive(context.chatSessionContext.chatSessionResource);
				const inputState = await this._chatSessions.getInputStateForSession(
					sessionResource,
					context.chatSessionContext.initialSessionOptions,
					token,
				);
				chatSessionContext = {
					chatSessionItem: {
						resource: sessionResource,
						label: context.chatSessionContext.isUntitled ? 'Untitled Session' : 'Session',
					},
					isUntitled: context.chatSessionContext.isUntitled,
					initialSessionOptions: context.chatSessionContext.initialSessionOptions,
					inputState,
				};
			}

			const chatContext: vscode.ChatContext = {
				history,
				chatSessionContext,
				get yieldRequested() { return inFlightRequest?.yieldRequested ?? false; }
			};
			const task = agent.invoke(
				extRequest,
				chatContext,
				stream.apiObject,
				token
			);

			return await raceCancellationWithTimeout(1000, Promise.resolve(task).then((result) => {
				if (result?.metadata) {
					try {
						JSON.stringify(result.metadata);
					} catch (err) {
						const msg = `result.metadata MUST be JSON.stringify-able. Got error: ${err.message}`;
						this._logService.error(`[${agent.extension.identifier.value}] [@${agent.id}] ${msg}`, agent.extension);
						return { errorDetails: { message: msg }, timings: stream?.timings, nextQuestion: result.nextQuestion, };
					}
				}
				let errorDetails: IChatResponseErrorDetails | undefined;
				if (result?.errorDetails) {
					errorDetails = {
						...result.errorDetails,
						responseIsIncomplete: true
					};
				}
				if (errorDetails?.responseIsRedacted || errorDetails?.isQuotaExceeded || errorDetails?.isRateLimited || errorDetails?.confirmationButtons || errorDetails?.code) {
					checkProposedApiEnabled(agent.extension, 'chatParticipantPrivate');
				}

				return { errorDetails, timings: stream?.timings, metadata: result?.metadata, nextQuestion: result?.nextQuestion, details: result?.details } satisfies IChatAgentResult;
			}), token);
		} catch (e) {
			this._logService.error(e, agent.extension);

			if (e instanceof extHostTypes.LanguageModelError && e.cause) {
				e = e.cause;
			}

			const isQuotaExceeded = e instanceof Error && e.name === 'ChatQuotaExceeded';
			const isRateLimited = e instanceof Error && e.name === 'ChatRateLimited';
			const { callstack: errorCallstack } = packErrorForTelemetry(e);
			const errorName = e instanceof Error ? e.name : undefined;
			return { errorDetails: { message: toErrorMessage(e), responseIsIncomplete: true, isQuotaExceeded, isRateLimited }, errorCallstack, errorName };

		} finally {
			if (inFlightRequest) {
				this._inFlightRequests.delete(inFlightRequest);
			}
			// Clean up any pending carousel resolvers for this request
			const pendingResolvers = this._pendingCarouselResolvers.get(requestDto.requestId);
			if (pendingResolvers) {
				for (const deferred of pendingResolvers.values()) {
					deferred.complete(undefined);
				}
				this._pendingCarouselResolvers.delete(requestDto.requestId);
			}
			stream?.close();
		}
	}

	private getDiagnosticsWhenEnabled(extension: Readonly<IRelaxedExtensionDescription>) {
		if (!isProposedApiEnabled(extension, 'chatReferenceDiagnostic')) {
			return [];
		}
		return this._diagnostics.getDiagnostics();
	}

	private async getToolsForRequest(extension: IExtensionDescription, tools: UserSelectedTools | undefined, modelId: string, token: CancellationToken): Promise<Map<vscode.LanguageModelToolInformation, boolean>> {
		if (!tools) {
			return new Map();
		}
		const result = new Map<vscode.LanguageModelToolInformation, boolean>();
		for (const tool of this._tools.getTools(extension)) {
			if (typeof tools[tool.name] === 'boolean') {
				result.set(tool, tools[tool.name]);
			}
		}
		return result;
	}

	private async prepareHistoryTurns(extension: Readonly<IRelaxedExtensionDescription>, agentId: string, context: { history: IChatAgentHistoryEntryDto[] }): Promise<(vscode.ChatRequestTurn | vscode.ChatResponseTurn)[]> {
		const res: (vscode.ChatRequestTurn | vscode.ChatResponseTurn)[] = [];

		for (const h of context.history) {
			const ehResult = typeConvert.ChatAgentResult.to(h.result);
			const result: vscode.ChatResult = agentId === h.request.agentId || (isBuiltinParticipant(h.request.agentId) && isBuiltinParticipant(agentId)) ?
				ehResult :
				{ ...ehResult, metadata: undefined };

			// REQUEST turn
			const varsWithoutTools: vscode.ChatPromptReference[] = [];
			const toolReferences: vscode.ChatLanguageModelToolReference[] = [];
			for (const v of h.request.variables.variables) {
				if (v.kind === 'tool') {
					toolReferences.push(typeConvert.ChatLanguageModelToolReference.to(v));
				} else if (v.kind === 'toolset') {
					toolReferences.push(...v.value.map(typeConvert.ChatLanguageModelToolReference.to));
				} else {
					const ref = typeConvert.ChatPromptReference.to(v, this.getDiagnosticsWhenEnabled(extension), this._logService);
					if (ref) {
						varsWithoutTools.push(ref);
					}
				}
			}

			const editedFileEvents = isProposedApiEnabled(extension, 'chatParticipantPrivate') ? h.request.editedFileEvents : undefined;
			const modeInstructions2 = isProposedApiEnabled(extension, 'chatParticipantPrivate') && h.request.modeInstructions ? typeConvert.ChatRequestModeInstructions.to(h.request.modeInstructions) : undefined;
			const turn = new extHostTypes.ChatRequestTurn(h.request.message, h.request.command, varsWithoutTools, h.request.agentId, toolReferences, editedFileEvents, h.request.requestId, undefined, modeInstructions2);
			res.push(turn);

			// RESPONSE turn
			const parts = coalesce(h.response.map(r => typeConvert.ChatResponsePart.toContent(r, this._commands.converter)));
			res.push(new extHostTypes.ChatResponseTurn(parts, result, h.request.agentId, h.request.command));
		}

		return res;
	}

	$releaseSession(sessionResourceDto: UriComponents): void {
		const sessionResource = URI.revive(sessionResourceDto);
		this._sessionDisposables.deleteAndDispose(sessionResource);
		const sessionId = LocalChatSessionUri.parseLocalSessionId(sessionResource);
		if (sessionId) {
			this._onDidDisposeChatSession.fire(sessionId);
		}
	}

	$acceptActiveChatSession(sessionResourceDto: UriComponents | undefined): void {
		const sessionResource = sessionResourceDto ? URI.revive(sessionResourceDto) : undefined;
		if (this._activeChatPanelSessionResource?.toString() === sessionResource?.toString()) {
			return;
		}

		this._activeChatPanelSessionResource = sessionResource;
		this._onDidChangeActiveChatPanelSessionResource.fire(sessionResource);
	}

	async $provideFollowups(requestDto: Dto<IChatAgentRequest>, handle: number, result: IChatAgentResult, context: { history: IChatAgentHistoryEntryDto[] }, token: CancellationToken): Promise<IChatFollowup[]> {
		const agent = this._agents.get(handle);
		if (!agent) {
			return Promise.resolve([]);
		}

		const request = revive<IChatAgentRequest>(requestDto);
		const convertedHistory = await this.prepareHistoryTurns(agent.extension, agent.id, context);

		const ehResult = typeConvert.ChatAgentResult.to(result);
		return (await agent.provideFollowups(ehResult, { history: convertedHistory, yieldRequested: false }, token))
			.filter(f => {
				// The followup must refer to a participant that exists from the same extension
				const isValid = !f.participant || Iterable.some(
					this._agents.values(),
					a => a.id === f.participant && ExtensionIdentifier.equals(a.extension.identifier, agent.extension.identifier));
				if (!isValid) {
					this._logService.warn(`[@${agent.id}] ChatFollowup refers to an unknown participant: ${f.participant}`);
				}
				return isValid;
			})
			.map(f => typeConvert.ChatFollowup.from(f, request));
	}

	$acceptFeedback(handle: number, result: IChatAgentResult, voteAction: IChatVoteAction): void {
		const agent = this._agents.get(handle);
		if (!agent) {
			return;
		}

		const ehResult = typeConvert.ChatAgentResult.to(result);
		let kind: extHostTypes.ChatResultFeedbackKind;
		switch (voteAction.direction) {
			case ChatAgentVoteDirection.Down:
				kind = extHostTypes.ChatResultFeedbackKind.Unhelpful;
				break;
			case ChatAgentVoteDirection.Up:
				kind = extHostTypes.ChatResultFeedbackKind.Helpful;
				break;
		}

		const feedback: vscode.ChatResultFeedback = {
			result: ehResult,
			kind,
		};
		agent.acceptFeedback(Object.freeze(feedback));
	}

	$handleQuestionCarouselAnswer(requestId: string, resolveId: string, answers: Record<string, unknown> | undefined): void {
		const requestResolvers = this._pendingCarouselResolvers.get(requestId);
		if (!requestResolvers) {
			return;
		}

		const deferred = requestResolvers.get(resolveId);
		if (deferred) {
			deferred.complete(answers);
			requestResolvers.delete(resolveId);
		}

		// Clean up if no more resolvers for this request
		if (requestResolvers.size === 0) {
			this._pendingCarouselResolvers.delete(requestId);
		}
	}

	$acceptAction(handle: number, result: IChatAgentResult, event: IChatUserActionEvent): void {
		const agent = this._agents.get(handle);
		if (!agent) {
			return;
		}
		if (event.action.kind === 'vote') {
			// handled by $acceptFeedback
			return;
		}

		const ehAction = typeConvert.ChatAgentUserActionEvent.to(result, event, this._commands.converter);
		if (ehAction) {
			agent.acceptAction(Object.freeze(ehAction));
		}
	}

	async $invokeCompletionProvider(handle: number, query: string, token: CancellationToken): Promise<IChatAgentCompletionItem[]> {
		const agent = this._agents.get(handle);
		if (!agent) {
			return [];
		}

		let disposables = this._completionDisposables.get(handle);
		if (disposables) {
			// Clear any disposables from the last invocation of this completion provider
			disposables.clear();
		} else {
			disposables = new DisposableStore();
			this._completionDisposables.set(handle, disposables);
		}

		const items = await agent.invokeCompletionProvider(query, token);

		return items.map((i) => typeConvert.ChatAgentCompletionItem.from(i, this._commands.converter, disposables));
	}

	async $provideChatTitle(handle: number, context: IChatAgentHistoryEntryDto[], token: CancellationToken): Promise<string | undefined> {
		const agent = this._agents.get(handle);
		if (!agent) {
			return;
		}

		const history = await this.prepareHistoryTurns(agent.extension, agent.id, { history: context });
		const sessionResource = context[0]?.request.sessionResource ? URI.revive(context[0].request.sessionResource) : undefined;
		return await agent.provideTitle({ history, sessionResource, yieldRequested: false }, token);
	}

	async $provideChatSummary(handle: number, context: IChatAgentHistoryEntryDto[], token: CancellationToken): Promise<string | undefined> {
		const agent = this._agents.get(handle);
		if (!agent) {
			return;
		}

		const history = await this.prepareHistoryTurns(agent.extension, agent.id, { history: context });
		const sessionResource = context[0]?.request.sessionResource ? URI.revive(context[0].request.sessionResource) : undefined;
		return await agent.provideSummary({ history, sessionResource, yieldRequested: false }, token);
	}
}

class ExtHostParticipantDetector {
	constructor(
		public readonly extension: IExtensionDescription,
		public readonly provider: vscode.ChatParticipantDetectionProvider,
	) { }
}

class ExtHostChatAgent {

	private _followupProvider: vscode.ChatFollowupProvider | undefined;
	private _iconPath: vscode.Uri | { light: vscode.Uri; dark: vscode.Uri } | vscode.ThemeIcon | undefined;
	private _helpTextPrefix: string | vscode.MarkdownString | undefined;
	private _helpTextPostfix: string | vscode.MarkdownString | undefined;
	private _onDidReceiveFeedback = new Emitter<vscode.ChatResultFeedback>();
	private _onDidPerformAction = new Emitter<vscode.ChatUserActionEvent>();
	private _supportIssueReporting: boolean | undefined;
	private _agentVariableProvider?: { provider: vscode.ChatParticipantCompletionItemProvider; triggerCharacters: string[] };
	private _additionalWelcomeMessage?: string | vscode.MarkdownString | undefined;
	private _titleProvider?: vscode.ChatTitleProvider | undefined;
	private _summarizer?: vscode.ChatSummarizer | undefined;
	private _pauseStateEmitter = new Emitter<vscode.ChatParticipantPauseStateEvent>();

	constructor(
		public readonly extension: IExtensionDescription,
		public readonly id: string,
		private readonly _proxy: MainThreadChatAgentsShape2,
		private readonly _handle: number,
		private _requestHandler: vscode.ChatExtendedRequestHandler,
	) { }

	acceptFeedback(feedback: vscode.ChatResultFeedback) {
		this._onDidReceiveFeedback.fire(feedback);
	}

	acceptAction(event: vscode.ChatUserActionEvent) {
		this._onDidPerformAction.fire(event);
	}

	setChatRequestPauseState(pauseState: vscode.ChatParticipantPauseStateEvent) {
		this._pauseStateEmitter.fire(pauseState);
	}

	async invokeCompletionProvider(query: string, token: CancellationToken): Promise<vscode.ChatCompletionItem[]> {
		if (!this._agentVariableProvider) {
			return [];
		}

		return await this._agentVariableProvider.provider.provideCompletionItems(query, token) ?? [];
	}

	async provideFollowups(result: vscode.ChatResult, context: vscode.ChatContext, token: CancellationToken): Promise<vscode.ChatFollowup[]> {
		if (!this._followupProvider) {
			return [];
		}

		const followups = await this._followupProvider.provideFollowups(result, context, token);
		if (!followups) {
			return [];
		}
		return followups
			// Filter out "command followups" from older providers
			.filter(f => !(f && 'commandId' in f))
			// Filter out followups from older providers before 'message' changed to 'prompt'
			.filter(f => !(f && 'message' in f));
	}

	async provideTitle(context: vscode.ChatContext, token: CancellationToken): Promise<string | undefined> {
		if (!this._titleProvider) {
			return;
		}

		return await this._titleProvider.provideChatTitle(context, token) ?? undefined;
	}

	async provideSummary(context: vscode.ChatContext, token: CancellationToken): Promise<string | undefined> {
		if (!this._summarizer) {
			return;
		}

		return await this._summarizer.provideChatSummary(context, token) ?? undefined;
	}

	get apiAgent(): vscode.ChatParticipant {
		let disposed = false;
		let updateScheduled = false;
		const updateMetadataSoon = () => {
			if (disposed) {
				return;
			}
			if (updateScheduled) {
				return;
			}
			updateScheduled = true;
			queueMicrotask(() => {
				this._proxy.$updateAgent(this._handle, {
					icon: !this._iconPath ? undefined :
						this._iconPath instanceof URI ? this._iconPath :
							'light' in this._iconPath ? this._iconPath.light :
								undefined,
					iconDark: !this._iconPath ? undefined :
						'dark' in this._iconPath ? this._iconPath.dark :
							undefined,
					themeIcon: this._iconPath instanceof extHostTypes.ThemeIcon ? this._iconPath : undefined,
					hasFollowups: this._followupProvider !== undefined,
					helpTextPrefix: (!this._helpTextPrefix || typeof this._helpTextPrefix === 'string') ? this._helpTextPrefix : typeConvert.MarkdownString.from(this._helpTextPrefix),
					helpTextPostfix: (!this._helpTextPostfix || typeof this._helpTextPostfix === 'string') ? this._helpTextPostfix : typeConvert.MarkdownString.from(this._helpTextPostfix),
					supportIssueReporting: this._supportIssueReporting,
					additionalWelcomeMessage: (!this._additionalWelcomeMessage || typeof this._additionalWelcomeMessage === 'string') ? this._additionalWelcomeMessage : typeConvert.MarkdownString.from(this._additionalWelcomeMessage),
				});
				updateScheduled = false;
			});
		};

		const that = this;
		return {
			get id() {
				return that.id;
			},
			get iconPath() {
				return that._iconPath;
			},
			set iconPath(v) {
				that._iconPath = v;
				updateMetadataSoon();
			},
			get requestHandler() {
				return that._requestHandler;
			},
			set requestHandler(v) {
				assertType(typeof v === 'function', 'Invalid request handler');
				that._requestHandler = v;
			},
			get followupProvider() {
				return that._followupProvider;
			},
			set followupProvider(v) {
				that._followupProvider = v;
				updateMetadataSoon();
			},
			get helpTextPrefix() {
				checkProposedApiEnabled(that.extension, 'defaultChatParticipant');
				return that._helpTextPrefix;
			},
			set helpTextPrefix(v) {
				checkProposedApiEnabled(that.extension, 'defaultChatParticipant');
				that._helpTextPrefix = v;
				updateMetadataSoon();
			},
			get helpTextPostfix() {
				checkProposedApiEnabled(that.extension, 'defaultChatParticipant');
				return that._helpTextPostfix;
			},
			set helpTextPostfix(v) {
				checkProposedApiEnabled(that.extension, 'defaultChatParticipant');
				that._helpTextPostfix = v;
				updateMetadataSoon();
			},
			get supportIssueReporting() {
				checkProposedApiEnabled(that.extension, 'chatParticipantPrivate');
				return that._supportIssueReporting;
			},
			set supportIssueReporting(v) {
				checkProposedApiEnabled(that.extension, 'chatParticipantPrivate');
				that._supportIssueReporting = v;
				updateMetadataSoon();
			},
			get onDidReceiveFeedback() {
				return that._onDidReceiveFeedback.event;
			},
			set participantVariableProvider(v) {
				checkProposedApiEnabled(that.extension, 'chatParticipantAdditions');
				that._agentVariableProvider = v;
				if (v) {
					if (!v.triggerCharacters.length) {
						throw new Error('triggerCharacters are required');
					}

					that._proxy.$registerAgentCompletionsProvider(that._handle, that.id, v.triggerCharacters);
				} else {
					that._proxy.$unregisterAgentCompletionsProvider(that._handle, that.id);
				}
			},
			get participantVariableProvider() {
				checkProposedApiEnabled(that.extension, 'chatParticipantAdditions');
				return that._agentVariableProvider;
			},
			set additionalWelcomeMessage(v) {
				checkProposedApiEnabled(that.extension, 'defaultChatParticipant');
				that._additionalWelcomeMessage = v;
				updateMetadataSoon();
			},
			get additionalWelcomeMessage() {
				checkProposedApiEnabled(that.extension, 'defaultChatParticipant');
				return that._additionalWelcomeMessage;
			},
			set titleProvider(v) {
				checkProposedApiEnabled(that.extension, 'defaultChatParticipant');
				that._titleProvider = v;
				updateMetadataSoon();
			},
			get titleProvider() {
				checkProposedApiEnabled(that.extension, 'defaultChatParticipant');
				return that._titleProvider;
			},
			set summarizer(v) {
				checkProposedApiEnabled(that.extension, 'defaultChatParticipant');
				that._summarizer = v;
			},
			get summarizer() {
				checkProposedApiEnabled(that.extension, 'defaultChatParticipant');
				return that._summarizer;
			},
			get onDidChangePauseState() {
				checkProposedApiEnabled(that.extension, 'chatParticipantAdditions');
				return that._pauseStateEmitter.event;
			},
			onDidPerformAction: !isProposedApiEnabled(this.extension, 'chatParticipantAdditions')
				? undefined!
				: this._onDidPerformAction.event
			,
			dispose() {
				disposed = true;
				that._followupProvider = undefined;
				that._onDidReceiveFeedback.dispose();
				that._onDidPerformAction.dispose();
				that._pauseStateEmitter.dispose();
				that._proxy.$unregisterAgent(that._handle);
			},
		} satisfies vscode.ChatParticipant;
	}

	invoke(request: vscode.ChatRequest, context: vscode.ChatContext, response: vscode.ChatResponseStream, token: CancellationToken): vscode.ProviderResult<vscode.ChatResult | void> {
		return this._requestHandler(request, context, response, token);
	}
}

/**
 * raceCancellation, but give the promise a little time to complete to see if we can get a real result quickly.
 */
function raceCancellationWithTimeout<T>(cancelWait: number, promise: Promise<T>, token: CancellationToken): Promise<T | undefined> {
	return new Promise((resolve, reject) => {
		const ref = token.onCancellationRequested(async () => {
			ref.dispose();
			await timeout(cancelWait);
			resolve(undefined);
		});
		promise.then(resolve, reject).finally(() => ref.dispose());
	});
}

function isBuiltinParticipant(agentId: string): boolean {
	return agentId.startsWith('github.copilot');
}
